import React, {
  useContext, useEffect, useState, useCallback, useMemo,
} from 'react';
import { Navigation, NavigationItem } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { withRouter } from '@americanexpress/one-app-router';
import { PaymentContext } from '../utility/PaymentContext';
import { autoPayFrequencyType, autoPayOptions } from '../assets/statementData';
import useApiWithSession from '../utility/useApiWithSession';
import { TokenContext } from '../utility/TokenContext';
import PopupModal from './PopupModal';

// Helper: Parse date string as local midnight if in YYYY-MM-DD format, and return
// formatted string
const parseLocalDate = (dateStr) => {
  if (!dateStr) return '';
  let dateObj;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    dateObj = new Date(`${dateStr}T00:00:00`);
  } else {
    dateObj = new Date(dateStr);
  }
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};
// Helper: Format processedTimestamp as 'YYYY-MM-DD HH:mm:ss'
const formatProcessedTimestamp = (ts) => {
  if (!ts) return 'N/A';
  if (ts.includes('T')) {
    const [date, time] = ts.split('T');
    return `${parseLocalDate(date)} - ${time.split('.')[0]} MST`;
  }
  return ts;
};

const NavigationComponent = ({ router }) => {
  const {
    paymentResponse, market1, journey1, sid1, u1,
  } = useContext(PaymentContext);
  const [modalOpen, setModalOpen] = useState(false);
  const { gateWayUrl } = useContext(TokenContext);

  // API hooks
  const {
    isLoading, error, data,
    run: runPhoneCall,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'HAMBURGER_CONTINUE_PHONE' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });
  const {
    isLoading: phoneCallLoading, error: phoneError, data: phoneData, run: runRepresentativeCall,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'HAMBURGER_CCP' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  // Navigation effects for API responses
  useEffect(() => {
    if (isLoading === false) {
      if (!error && data?.statusCode === 'success') {
        router.push({ pathname: '/message', query: { level: 1 } });
      } else if (error || data?.statusCode === 'failure') {
        router.push({ pathname: '/message', query: { level: 3 } });
      }
    }
  }, [isLoading, error, data, router]);

  useEffect(() => {
    if (phoneCallLoading === false) {
      if (!phoneError && phoneData?.statusCode === 'success') {
        router.push({ pathname: '/message', query: { level: 1 } });
      } else if (phoneError || phoneData?.statusCode === 'failure') {
        router.push({ pathname: '/message', query: { level: 3 } });
      }
    }
  }, [phoneCallLoading, phoneError, phoneData, router]);

  // Handlers
  const phoneCall = useCallback(async () => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!sessionId) return;
    await runPhoneCall();
  }, [runPhoneCall]);

  const representativeCall = useCallback(async () => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!sessionId) return;
    await runRepresentativeCall();
  }, [runRepresentativeCall]);

  // Memoized payment details
  const remittanceDetails = useMemo(() => paymentResponse?.paymentHistory?.remittances
      || [], [paymentResponse]);

  const autoPaymentDetails = useMemo(() => {
    const autoPayRemittances = remittanceDetails
      .filter((remittance) => remittance?.paymentType === 'AUTOPAY'
        && (remittance?.status === 'ACTIVE' || remittance?.status === 'PROCESSING')
        && remittance?.postingStatus !== 'POSTED'
        && remittance?.frequency !== 'IMMEDIATE')
      .slice(0, 2);
    return {
      title: 'Auto Payment Details',
      subtitle: ['Frequency', 'Option', 'Payment Date', 'Amount', 'Threshold Amount'],
      remittances: autoPayRemittances.map((remittance) => [
        remittance?.frequency ? autoPayFrequencyType[remittance?.frequency] : 'N/A',
        remittance?.option && remittance?.option !== 'FIXED_AMOUNT'
          ? autoPayOptions[remittance?.option]
          : 'N/A',
        remittance?.frequency !== 'EXPOSURE'
          ? parseLocalDate(remittance?.paymentDate) || 'N/A'
          : 'N/A',
        remittance?.option === 'FIXED_AMOUNT'
          ? `$${Number(remittance?.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : 'N/A',
        remittance?.frequency === 'EXPOSURE'
          ? `$${Number(remittance?.thresholdAmount?.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : 'N/A',
      ]),
    };
  }, [remittanceDetails]);

  const scheduledPaymentDetails = useMemo(() => {
    const scheduledRemittances = remittanceDetails
      .filter((remittance) => remittance?.frequency === 'SCHEDULED'
        && remittance?.source === 'PAYBILL'
        && (remittance?.paymentType === 'PAYBILL' || remittance?.program === 'WEB_COLLECTIONS')
        && (remittance?.status === 'ACTIVE' || remittance?.status === 'PROCESSING'));
    return {
      title: 'Scheduled Payment Details',
      subtitle: ['Payment Date', 'Amount'],
      remittances: scheduledRemittances.map((remittance) => [
        parseLocalDate(remittance?.paymentDate) || 'N/A',
        `$${Number(remittance?.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ]),
    };
  }, [remittanceDetails]);

  const recentPaymentDetails = useMemo(() => {
    const filteredRecentRemittances = remittanceDetails.filter(
      (remittance) => (remittance?.status === 'PROCESSING' || remittance?.status === 'PENDING')
        && Number(remittance?.amount) > 0
    );
    return {
      title: 'Recent Payment Details',
      subtitle: ['Payment Type', 'Amount', 'Payment Date'],
      remittances: filteredRecentRemittances.map((remittance) => [
        remittance?.paymentType || 'N/A',
        `$${Number(remittance?.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        formatProcessedTimestamp(remittance.processedTimestamp),
      ]),
    };
  }, [remittanceDetails]);

  const hasAnyPaymentDetails = autoPaymentDetails.remittances.length > 0
    || scheduledPaymentDetails.remittances.length > 0
    || recentPaymentDetails.remittances.length > 0;

  return (
    <Navigation
      defaultOpenPanelIds={hasAnyPaymentDetails ? ['payment-details', 'get-help'] : ['get-help']}
      shouldAllowMultipleExpanded={true}
      showInContainer={true}
    >
      {hasAnyPaymentDetails && (
        <NavigationItem id="payment-details" label="Payment Details">
          {autoPaymentDetails.remittances.length > 0 && (
            <NavigationItem
              id="auto-payment-details"
              label={autoPaymentDetails.title}
            >
              <div className="pad-4-lr pad-1-tb flex flex-column" style={{ gap: '1rem' }}>
                {autoPaymentDetails.remittances.map((remit) => {
                  // Use paymentDate + option + amount as a unique key
                  const remitKey = `${remit[2] || ''}-${remit[1] || ''}-${remit[3] || ''}`;
                  return (
                    <div key={remitKey} className="pad-1-b">
                      {autoPaymentDetails.subtitle.map((sub, subIdx) => remit[subIdx] !== 'N/A' && (
                        <p key={`auto-remit-${sub}-${remitKey}`} className="body-1">
                          {sub}: <strong>{remit[subIdx]}</strong>
                        </p>
                      ))}
                      <hr />
                    </div>
                  );
                })}
              </div>
            </NavigationItem>
          )}
          {scheduledPaymentDetails.remittances.length > 0 && (
            <NavigationItem
              id={scheduledPaymentDetails.title.toLowerCase().replace(/\s+/g, '-')}
              label={scheduledPaymentDetails.title}
            >
              <div className="pad-4-lr pad-1-tb flex flex-column" style={{ gap: '1rem' }}>
                {scheduledPaymentDetails.remittances.map((remit) => {
                  // Use paymentDate + amount as a unique key
                  const remitKey = `${remit[0] || ''}-${remit[1] || ''}`;
                  return (
                    <div key={remitKey} className="pad-1-b">
                      {scheduledPaymentDetails.subtitle.map((sub, subIdx) => remit[subIdx] !== 'N/A' && (
                        <p key={`scheduled-remit-${sub}-${remitKey}`} className="body-1">
                          {sub}: <strong>{remit[subIdx]}</strong>
                        </p>
                      ))}
                      <hr />
                    </div>
                  );
                })}
              </div>
            </NavigationItem>
          )}
          {recentPaymentDetails.remittances.length > 0 && (
            <NavigationItem
              id={recentPaymentDetails.title.toLowerCase().replace(/\s+/g, '-')}
              label={recentPaymentDetails.title}
            >
              <div className="pad-4-lr pad-1-tb flex flex-column" style={{ gap: '1rem' }}>
                {recentPaymentDetails.remittances.map((remit) => {
                  // Use paymentType + amount + processedTimestamp as a unique key
                  const remitKey = `${remit[0] || ''}-${remit[1] || ''}-${remit[2] || ''}`;
                  return (
                    <div key={remitKey} className="pad-1-b">
                      {recentPaymentDetails.subtitle.map((sub, subIdx) => remit[subIdx] !== 'N/A' && (
                        <p key={`recent-remit-${sub}-${remitKey}`} className="body-1">
                          {sub}: <strong>{remit[subIdx]}</strong>
                        </p>
                      ))}
                      <hr />
                    </div>
                  );
                })}
              </div>
            </NavigationItem>
          )}
        </NavigationItem>
      )}
      <NavigationItem id="get-help" label="Get Help">
        <NavigationItem onClick={phoneCall} label="Continue on Phone" />
        <NavigationItem onClick={() => setModalOpen(true)} label="Talk to Representative" />
      </NavigationItem>
      {modalOpen && (
        <PopupModal
          content="Talk to Customer Care Professional now?"
          action={representativeCall}
          id="call"
          isModalOpen={modalOpen}
          setModalOpen={setModalOpen}
        />
      )}
    </Navigation>
  );
};

export default withRouter(NavigationComponent);

NavigationComponent.propTypes = {
  router: PropTypes.instanceOf(Object).isRequired,
};
